// SPDX-FileCopyrightText: 2026 Stadtplatz.de
//
// SPDX-License-Identifier: EUPL-1.2

import React, { useState, useEffect } from "react";
import { Loader2, Save, Shield } from "lucide-react";
import { useNotification } from "../../context/NotificationContext";
import { tenantSettingsService } from "../../services/tenantSettingsService";
import useCaseService from "../../services/useCaseService";
import { usePermissions } from "../../hooks/usePermissions";
import { useRoutes } from "../../hooks/useRoutes";
import { hasSuperAdminAccess, hasAdminAccess } from "../../config/roleConfig";
import GlobalAdminHeader from "../../components/admin/GlobalAdminHeader";
import useTenants from "../../hooks/useTenants";
import { getAdminTenantConfig } from "../../utils/tenantConfig";
import PageLoader from "../../components/PageLoader";

// Build the editable Use Cases list from the real catalogue (GET /admin/use-cases/),
// overlaying any per-tenant enabled / approval_required state saved in the booking
// config. Saved entries are matched back to the catalogue by id (or code as fallback).
const mergeUseCases = (apiUseCases = [], savedUseCases = []) => {
  const savedById = new Map(
    (Array.isArray(savedUseCases) ? savedUseCases : []).map((u) => [
      String(u.id ?? u.code),
      u,
    ]),
  );
  return apiUseCases.map((uc) => {
    const saved =
      savedById.get(String(uc.id)) ?? savedById.get(String(uc.code));
    return {
      id: uc.id,
      code: uc.code,
      name: uc.name,
      enabled: saved?.enabled ?? true,
      approval_required: saved?.approval_required ?? false,
    };
  });
};

const BuchungenSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({});
  const [useCases, setUseCases] = useState([]);
  const { showSuccess, showError } = useNotification();
  const { hasPermission } = usePermissions();
  const { userRoles, userInfo } = useRoutes();
  const isSuperAdmin = hasSuperAdminAccess(userRoles || []);
  const isAdmin = hasAdminAccess(userRoles || []);
  let selectednewTenant = null;
  if (isAdmin) {
    selectednewTenant = userInfo.tenant_id || null;
  }
  const {
    tenantOptions,
    selectedTenant,
    setSelectedTenant,
    loading: tenantsLoading,
  } = useTenants({
    ...getAdminTenantConfig(isSuperAdmin),
    includeAll: false,
  });

  const loadSettings = async () => {
    try {
      setLoading(true);

      // Real use case catalogue is tenant-independent. Fetch it up front; a
      // failure here shouldn't block the rest of the settings from loading.
      let apiUseCases = [];
      try {
        const resp = await useCaseService.listUseCases();
        apiUseCases = Array.isArray(resp?.items)
          ? resp.items
          : Array.isArray(resp)
            ? resp
            : [];
      } catch (err) {
        console.error("Error loading use cases:", err);
      }

      if ((isSuperAdmin || isAdmin) && !selectedTenant && !selectednewTenant) {
        setSettings({});
        setUseCases(mergeUseCases(apiUseCases, []));
        return;
      }

      const tenantId =
        isSuperAdmin || isAdmin ? selectedTenant || selectednewTenant : null;
      if (!tenantId) {
        setSettings({});
        setUseCases(mergeUseCases(apiUseCases, []));
        return;
      }

      const config = await tenantSettingsService.getTenantConfig(tenantId);
      const bookingConfig = config.booking || {};
      setSettings(bookingConfig);

      // Overlay per-tenant enabled / approval_required onto the real catalogue.
      setUseCases(mergeUseCases(apiUseCases, bookingConfig.use_cases));
    } catch (error) {
      console.error("Error loading booking settings:", error);
      showError("Fehler beim Laden der Buchungseinstellungen");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, [selectedTenant, isSuperAdmin]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const isCheckbox = type === "checkbox";
    setSettings((prev) => ({
      ...prev,
      [name]: isCheckbox ? checked : value,
    }));
  };

  const saveSettings = async () => {
    try {
      setSaving(true);
      if ((isSuperAdmin || isAdmin) && !selectedTenant && !selectednewTenant) {
        showError("Bitte zuerst einen Mandanten auswählen");
        return;
      }

      const tenantId =
        isSuperAdmin || isAdmin ? selectedTenant || selectednewTenant : null;
      if (!tenantId) {
        showError("Kein Mandant für das Speichern gefunden");
        return;
      }

      // Merge with existing config so other sections (branding, localization, etc.) stay intact
      const currentConfig =
        await tenantSettingsService.getTenantConfig(tenantId);
      const nextConfig = {
        ...currentConfig,
        booking: {
          ...settings,
          use_cases: useCases,
        },
      };

      await tenantSettingsService.updateTenantConfig(tenantId, nextConfig);
      showSuccess("Buchungseinstellungen erfolgreich gespeichert!");
      await loadSettings();
    } catch (error) {
      console.error("Error saving booking settings:", error);
      showError("Fehler beim Speichern der Einstellungen");
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    await saveSettings();
  };

  if (
    !isSuperAdmin &&
    !isAdmin &&
    !hasPermission("tenant_settings.manage") &&
    !hasPermission("tenant_settings.view")
  ) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="w-12 h-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
          <p className="text-gray-500 dark:text-slate-400">
            Keine Berechtigung für diese Einstellungen
          </p>
        </div>
      </div>
    );
  }

  const inputClass =
    "w-full px-3 py-2 border border-gray-300 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:placeholder-slate-400";

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <GlobalAdminHeader
        title="Buchungseinstellungen"
        subtitle="Automatische Genehmigung und Vorlaufzeiten"
        tenantOptions={isSuperAdmin ? tenantOptions : []}
        selectedTenant={isSuperAdmin ? selectedTenant : null}
        onTenantChange={isSuperAdmin ? setSelectedTenant : undefined}
        tenantsLoading={isSuperAdmin ? tenantsLoading : false}
      />
      <div className="mx-auto px-4 lg:px-6 sm:mt-6 mt-3 pb-8">
        {loading ? (
          <PageLoader message="Lade Einstellungen..." />
        ) : (
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow dark:border dark:border-slate-700 sm:p-6 p-4 w-full">
            <form className="space-y-4" onSubmit={handleSubmit}>
              <h3 className="text-lg font-semibold text-gray-800 dark:text-slate-100 mb-4">
                Buchungseinstellungen
              </h3>

              {/* Booking Toggle */}
              <div className="flex items-center justify-between dark:bg-slate-800 rounded">
                <div className="flex-1 mr-4">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">
                    Automatische Genehmigung
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Buchungen automatisch ohne manuelle Prüfung genehmigen
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    name="auto_approve"
                    checked={settings.auto_approve || false}
                    onChange={handleChange}
                  />
                  <div className="w-11 h-6 bg-gray-200 dark:bg-slate-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-color rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white dark:after:bg-slate-300 after:border-gray-300 dark:after:border-slate-500 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-color dark:peer-checked:bg-primary-color/40" />
                </label>
              </div>

              {/* Booking Time Settings */}
              <div className="grid grid-cols-3 gap-6 pb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Stornierungsfrist (Stunden)
                  </label>
                  <input
                    type="number"
                    name="cancellation_period"
                    value={settings.cancellation_period || ""}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="24"
                    min="0"
                  />
                  <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                    Zeitraum, in dem Buchungen storniert werden können
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Max. Vorlaufzeit (Tage)
                  </label>
                  <input
                    type="number"
                    name="max_advance_days"
                    value={settings.max_advance_days || ""}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="365"
                    min="1"
                  />
                  <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                    Wie weit im Voraus können Buchungen gemacht werden
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Min. Vorlaufzeit (Stunden)
                  </label>
                  <input
                    type="number"
                    name="min_advance_hours"
                    value={settings.min_advance_hours || ""}
                    onChange={handleChange}
                    className={inputClass}
                    placeholder="2"
                    min="0"
                  />
                  <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                    Mindestzeit vor der Buchung
                  </p>
                </div>
              </div>

              {/* Use Cases – real catalogue from GET /admin/use-cases/; the
                  enabled / approval_required toggles persist per tenant in the
                  booking config (see saveSettings). */}
              <div className="pt-5 border-t border-gray-200 dark:border-slate-600">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Use Cases
                </h3>
                {useCases.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-slate-400">
                    Keine Use Cases verfügbar.
                  </p>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {useCases.map((uc, idx) => (
                    <div
                      key={uc.id}
                      className="p-4 rounded-xl bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-600 flex flex-wrap items-center justify-between gap-3"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <label className="relative inline-flex items-center cursor-pointer shrink-0">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={uc.enabled}
                            onChange={(e) => {
                              const updated = [...useCases];
                              updated[idx] = {
                                ...updated[idx],
                                enabled: e.target.checked,
                              };
                              setUseCases(updated);
                            }}
                          />
                          <div className="w-11 h-6 bg-gray-200 dark:bg-slate-600 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-color rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white dark:after:bg-slate-300 after:border-gray-300 dark:after:border-slate-500 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-color dark:peer-checked:bg-primary-color/40" />
                        </label>
                        <span className="font-medium text-gray-900 dark:text-white truncate">
                          {uc.name}
                        </span>
                      </div>
                      <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 shrink-0 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={uc.approval_required}
                          onChange={(e) => {
                            const updated = [...useCases];
                            updated[idx] = {
                              ...updated[idx],
                              approval_required: e.target.checked,
                            };
                            setUseCases(updated);
                          }}
                          className="w-4 h-4 rounded border-gray-300 dark:border-slate-600 text-primary-color focus:ring-primary-color dark:bg-slate-700"
                        />
                        Genehmigung
                      </label>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => loadSettings()}
                  className="px-4 py-2 text-rose-700 dark:text-rose-200 border border-rose-300 dark:border-rose-700 bg-rose-100 dark:bg-rose-900/30 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/50 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex items-center px-4 py-2 bg-primary-color dark:bg-primary-color/40 text-white rounded-lg hover:bg-primary-color-dark dark:hover:bg-primary-color/40 transition-colors disabled:opacity-50"
                >
                  {saving ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-5 h-5 mr-2" />
                  )}
                  {saving ? "Speichern..." : "Einstellungen speichern"}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default BuchungenSettings;
